/**
 * Rendering an Ory self-service flow.
 *
 * Ory returns a flow as a list of **UI nodes** — inputs, buttons, messages —
 * and the client renders them. That indirection is the whole point of §6's "we
 * do not build authentication": this tenant currently offers `identifier_first`
 * login, and if it is later configured for passwords, passkeys, one-time codes
 * or a social provider, Ory sends different nodes and this code renders them
 * without being touched.
 *
 * The alternative — an email field and a password field, hardcoded — would work
 * today and would silently fail to offer whatever is enabled tomorrow. Worse,
 * it would encode a login method choice in a frontend, where nobody looking for
 * the authentication policy would think to look.
 */

import type {
  UiNode,
  UiNodeAnchorAttributes,
  UiNodeImageAttributes,
  UiNodeInputAttributes,
  UiNodeTextAttributes,
  UiText,
} from '@ory/client-fetch'

/**
 * A flow node, classified so the renderer can be exhaustive (#99).
 *
 * This used to be `input`-shaped only, and everything else was silently
 * dropped. That is a bigger hole than it sounds: **a TOTP enrolment consists
 * almost entirely of the other kinds.** Ory sends the QR code as an `img` node
 * (`totp_qr`) and the shared secret as a `text` node (`totp_secret_key`);
 * lookup-secret enrolment sends the recovery codes as `text`. Dropping them
 * left a form asking the customer to type a code from an authenticator they had
 * never been given the means to set up — so "add a second factor", which
 * Settings ships, could not be completed by any route, including manual entry.
 *
 * It is also where §6's rule quietly stopped being true. "We render whatever Ory
 * returns" is the whole argument for not hardcoding an email and password field,
 * and a renderer that handles one node type has hardcoded the answer "a method
 * is some inputs".
 *
 * `unsupported` is a case rather than a filter, deliberately: a node kind this
 * app cannot draw is said on screen. An unrenderable node dropped in silence is
 * the same failure this issue is about, one level down.
 */
export type RenderableNode =
  | { readonly kind: 'input'; readonly key: string; readonly node: UiNode; readonly attrs: UiNodeInputAttributes }
  | { readonly kind: 'text'; readonly key: string; readonly node: UiNode; readonly attrs: UiNodeTextAttributes }
  | { readonly kind: 'image'; readonly key: string; readonly node: UiNode; readonly attrs: UiNodeImageAttributes }
  | { readonly kind: 'anchor'; readonly key: string; readonly node: UiNode; readonly attrs: UiNodeAnchorAttributes }
  | { readonly kind: 'unsupported'; readonly key: string; readonly node: UiNode; readonly nodeType: string }

/** Node groups worth rendering, in the order they should appear. */
const GROUP_ORDER = [
  'default',
  'identifier_first',
  'password',
  'code',
  'passkey',
  'webauthn',
  'totp',
  'lookup_secret',
  'oidc',
  'profile',
  'link',
] as const

function groupRank(group: string): number {
  const i = (GROUP_ORDER as readonly string[]).indexOf(group)
  return i === -1 ? GROUP_ORDER.length : i
}

/**
 * Every node of a flow, classified and ordered.
 *
 * Hidden inputs are kept — `csrf_token` is one of them, and dropping it turns
 * every submission into a CSRF failure that reads like "wrong password".
 *
 * The sort is by GROUP and `Array.prototype.sort` is stable, so Ory's own order
 * survives within a group. That is what keeps a TOTP QR (group `totp`) directly
 * above the code field it belongs to (group `totp`) rather than floating to the
 * top of the form.
 */
export function renderableNodes(nodes: readonly UiNode[]): RenderableNode[] {
  return nodes
    .map((node, i): RenderableNode => {
      const key = `${node.type}-${i}`
      switch (node.type) {
        case 'input':
          return {
            kind: 'input',
            key: `${(node.attributes as UiNodeInputAttributes).name}-${i}`,
            node,
            attrs: node.attributes as UiNodeInputAttributes,
          }
        case 'text':
          return { kind: 'text', key, node, attrs: node.attributes as UiNodeTextAttributes }
        case 'img':
          return { kind: 'image', key, node, attrs: node.attributes as UiNodeImageAttributes }
        case 'a':
          return { kind: 'anchor', key, node, attrs: node.attributes as UiNodeAnchorAttributes }
        default:
          // `script` lands here, and that is on purpose. Ory uses it for
          // WebAuthn, running vendor script is its own decision with its own
          // risk, and TOTP must not wait on it. Named on screen, not dropped.
          return { kind: 'unsupported', key, node, nodeType: String(node.type) }
      }
    })
    .sort((a, b) => groupRank(a.node.group) - groupRank(b.node.group))
}

/**
 * Just the input nodes, for callers that only submit values.
 *
 * Kept because "what fields does this flow have" is a real question — the
 * settings tests ask it — and because narrowing here is better than every
 * caller re-filtering on a string.
 */
export function inputNodes(
  nodes: readonly UiNode[],
): Extract<RenderableNode, { kind: 'input' }>[] {
  return renderableNodes(nodes).filter(
    (n): n is Extract<RenderableNode, { kind: 'input' }> => n.kind === 'input',
  )
}

/**
 * The visible text of a `text` node.
 *
 * Ory nests it as `attributes.text.text`, and for lookup-secret codes the list
 * arrives in `context` rather than in the sentence. Prefer the sentence; fall
 * back to the raw context so recovery codes are never rendered as an empty
 * element, which is dropping them again with extra steps.
 */
export function textOf(attrs: UiNodeTextAttributes): string {
  const t = attrs.text?.text
  if (typeof t === 'string' && t !== '') return t
  const ctx = attrs.text?.context
  return ctx === undefined || ctx === null ? '' : JSON.stringify(ctx)
}

/** Whether a node is a submit button rather than a field the user fills in. */
export function isSubmit(attrs: UiNodeInputAttributes): boolean {
  return attrs.type === 'submit' || attrs.type === 'button'
}

export function isHidden(attrs: UiNodeInputAttributes): boolean {
  return attrs.type === 'hidden'
}

/** The label Ory supplies for a node, or a sensible fallback. */
export function labelFor(node: UiNode): string {
  const t = node.meta?.label?.text
  if (typeof t === 'string' && t !== '') return t
  const name = (node.attributes as UiNodeInputAttributes).name
  return typeof name === 'string' ? name : ''
}

export type MessageSeverity = 'error' | 'info' | 'success'

export interface FlowMessage {
  readonly id: number
  readonly text: string
  readonly severity: MessageSeverity
}

function severityOf(t: UiText): MessageSeverity {
  return t.type === 'error' ? 'error' : t.type === 'success' ? 'success' : 'info'
}

/**
 * Every message Ory attached to the flow — both the flow-level ones and the
 * per-field ones.
 *
 * Field-level messages are collected here as well as rendered inline, because
 * "the field you cannot see is the one that is wrong" is a real failure with a
 * multi-step flow: on a narrow screen a validation error can be scrolled out of
 * view and the form looks like it did nothing.
 */
export function messagesOf(
  flowMessages: readonly UiText[] | undefined,
  nodes: readonly UiNode[],
): FlowMessage[] {
  const out: FlowMessage[] = []
  for (const m of flowMessages ?? []) {
    out.push({ id: m.id, text: m.text, severity: severityOf(m) })
  }
  for (const n of nodes) {
    for (const m of n.messages ?? []) {
      out.push({ id: m.id, text: m.text, severity: severityOf(m) })
    }
  }
  return out
}

/**
 * Ory refusing an action because the session is not fresh enough.
 *
 * Changing a password on a session established hours ago returns `403` with
 * `session_refresh_required`. That is not a failure to show the customer — it
 * means "prove it is you again". Rendering Ory's raw error here would read as
 * "something went wrong" for what is a deliberate and correct security control.
 */
/**
 * The `Response` an `@ory/client-fetch` failure is carrying, if any.
 *
 * One place, because everything this module gets wrong about Ory it gets wrong
 * by reading the error object rather than the response inside it. `ResponseError`
 * assigns exactly `response` and `name` — no `body`, no `status`, nothing else.
 */
export function responseOf(err: unknown): Response | null {
  if (typeof err !== 'object' || err === null) return null
  const e = err as { response?: unknown }
  return e.response instanceof Response ? e.response : null
}

/**
 * Where Ory is asking the browser to go.
 *
 * `redirect_browser_to`, a **body** field on `ErrorBrowserLocationChangeRequired`.
 * `FlowForm` read it from a `Location` header, which Ory does not set — so it
 * always found nothing and the customer got a generic error in place of the
 * redirect (#29).
 *
 * The 303 branch could never have run either: `@ory/client-fetch` never sets
 * `redirect` on its fetch, so redirects are followed and the client sees the
 * followed response. `redirect: 'manual'` is not the remedy — a browser then
 * yields an opaque-redirect response with status 0 and an **empty header list**,
 * so the header stays unreadable, same-origin or not.
 *
 * The header is still consulted after the body, at no cost, so a
 * standards-shaped redirect from some future proxy is not discarded.
 */
export async function redirectTarget(err: unknown): Promise<string | null> {
  const res = responseOf(err)
  if (res === null) return null
  if (res.status !== 422 && res.status !== 303) return null
  try {
    const body = (await res.clone().json()) as { redirect_browser_to?: unknown }
    const to = body?.redirect_browser_to
    if (typeof to === 'string' && to !== '') return to
  } catch {
    // Not JSON. Fall through to the header.
  }
  const loc = res.headers?.get?.('location')
  return typeof loc === 'string' && loc !== '' ? loc : null
}

/**
 * What Ory wants before it will let this customer change anything.
 *
 * Two different refusals with two different remedies (#47):
 *
 *   `refresh`  the session is genuine but too old for a privileged change.
 *              Ory wants the same factor again, and the login flow must be
 *              created with `?refresh=true` — without it, a request that
 *              already carries a valid session is answered
 *              `400 session_already_available` rather than a login form.
 *   `aal2`     the session is fresh enough but has not reached the second
 *              factor. Ory wants a DIFFERENT factor, and the login flow must
 *              be created with `?aal=aal2`. `refresh=true` does not produce
 *              one, so a customer with a second factor enrolled would be asked
 *              for their password again and refused again.
 *
 * `needsFreshSession` collapsed both into `true` with a comment asserting they
 * "have the identical remedy". They do not, and the SDK has carried the
 * distinction all along: `CreateBrowserLoginFlowRequest` has `refresh` and
 * `aal` as separate fields.
 */
export type StepUp = 'refresh' | 'aal2'

export async function stepUpRequired(err: unknown): Promise<StepUp | null> {
  const res = responseOf(err)
  if (res === null || res.status !== 403) return null

  // Read the BODY off the response, not `err.body`.
  //
  // `@ory/client-fetch` throws `ResponseError`, which assigns exactly two own
  // properties — `response` and `name`. There is no `body`, and `runtime.d.ts`
  // declares none. So this predicate read `undefined`, stringified it to `'""'`,
  // and returned false on every call it has ever received: the password-change
  // refresh path documented in `Settings.tsx` has never once run (#29).
  //
  // The SDK throws without consuming the body, so it is still there to read —
  // which is exactly what `continuationFlow` already does for a 400.
  try {
    const body = (await res.clone().json()) as unknown
    const text = JSON.stringify(body ?? '')
    // aal2 first when both appear. It is the stronger requirement, and asking
    // for a second factor when only a refresh was needed still satisfies the
    // refresh; the other way round does not.
    if (text.includes('session_aal2_required')) return 'aal2'
    if (text.includes('session_refresh_required')) return 'refresh'
    return null
  } catch {
    // The body is unreadable — not JSON, or consumed by something else. A 403
    // on a self-service flow means re-authenticate, so default to a remedy
    // rather than to none: sending someone to sign in they did not have to is a
    // moment's annoyance, and the other way round is a customer who cannot
    // change their password and is shown a raw error instead. That asymmetry is
    // the whole reason this predicate exists.
    //
    // `refresh` is the safe default of the two: it is the commoner refusal, and
    // asking for the same factor again cannot strand a customer who has no
    // second factor enrolled.
    return 'refresh'
  }
}

/**
 * Whether Ory refused for want of a stronger session, without saying which.
 *
 * Kept because that is a question worth asking on its own, and because it is
 * what the existing tests ask. Callers that have to ACT on the answer want
 * `stepUpRequired`, since the two refusals need different login flows.
 */
export async function needsFreshSession(err: unknown): Promise<boolean> {
  return (await stepUpRequired(err)) !== null
}

/**
 * Write `value` at a dotted path, creating the objects on the way.
 *
 * Ory names its trait nodes with dots — `traits.email`, and deeper for a schema
 * that nests — which is its convention for FORM encoding. `FlowForm` collects
 * from a real `<form>`, so that is exactly what `FormData` hands back.
 *
 * But the body then goes through `@ory/client-fetch`, whose generated
 * serialiser copies only the fields it knows:
 *
 *     return {
 *       'csrf_token': value['csrf_token'],
 *       'method':     value['method'],
 *       'password':   value['password'],
 *       'traits':     value['traits'],
 *       'transient_payload': value['transient_payload'],
 *     }
 *
 * A flat `traits.email` key is not one of them, so it was **dropped silently**
 * and Ory answered "Property email is missing." against a form the customer had
 * filled in. Nobody could create an account, and nobody ever had in a browser
 * (#40).
 *
 * Ory's HTTP API accepts either shape — flat posts fine as raw JSON. The wire
 * format was never the problem; the typed client between us and the wire was.
 */
export function setPath(target: Record<string, unknown>, path: string, value: string): void {
  const parts = path.split('.')
  let node = target
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i] as string
    const next = node[key]
    // Only descend into a plain object. If something non-object already sits
    // there the flow is asking for two incompatible things at one path, and
    // overwriting it would silently discard one of them.
    if (typeof next !== 'object' || next === null || Array.isArray(next)) {
      node[key] = {}
    }
    node = node[key] as Record<string, unknown>
  }
  node[parts[parts.length - 1] as string] = value
}

